/**
 * WHO RANG. Every call from a household the business already cleans for, newest first, across the
 * whole client book.
 *
 * `client_calls` has been filled by the phone seam since the fiftieth table was added and read in
 * exactly one place: one household's own page. So "did anybody ring this morning" was a question
 * with no answer unless somebody already knew whose page to open, which is the wrong way round.
 * `docs/handover.md` section 4 item 10 asked for this read.
 *
 * IT IS THE SECOND TAB OF THE FOLLOW-UPS SCREEN, at `/tasks/calls`, and NOT a word of its own in the
 * rail. "Who rang" and "what did somebody say she would do about it" are one question asked twice,
 * and a call nobody answered raises a follow-up on the other tab. The rail stops at eight.
 *
 * EVERY ROW IS A LINK TO THE HOUSEHOLD, because a call is never the thing you came for: it is how
 * you find whose page to open. A call nobody answered is coral, the same as it is on her own page,
 * because it is the one row here somebody has to act on.
 *
 * THERE IS NO AUDIO AND NO TRANSCRIPT TEXT ON THIS LIST. `erd.md` Part 6 refuses call recording by
 * name and the audio never leaves the phone system. What is here is whether there is something to
 * read, which is a yes or a no beside the row; the words themselves are on the household's own
 * page, where whoever reads them is already looking at whose call it was.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, listCalls, type CallList, type CallRow, type Me } from '../api.ts';
import { Card, MEASURE, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { MarkPills } from '../components/DoNotContact.tsx';
import { DataTable, FilterBar, useWideView, type DataColumn } from '../components/ListView.tsx';
import { Busy, EmptyNotice, ErrorNotice } from '../components/Notice.tsx';
import { OpenTheCall, opensCallPages } from '../components/OpenTheCall.tsx';
import { GapPill, TranscriptMark } from '../components/StatusPills.tsx';
import { addDays, callLengthWords, formatInstant, isDay, shortInstant } from '../format.ts';
import { CALL_OUTCOME_LABELS } from '../labels.ts';
import { OfficeTabs } from './Tasks.tsx';

/**
 * What the screen opens on: a fortnight back from the server's own day. The door's own default is
 * thirteen months, which is the right answer for a report and the wrong one for the question this
 * screen is asked, which is nearly always about this week.
 */
const DAYS_SHOWN = 13;

/** Nobody spoke to her. The three outcomes that mean somebody still has to ring back. */
function nobodySpokeToHer(row: CallRow): boolean {
  return row.outcome === 'missed' || row.outcome === 'abandoned' || row.outcome === 'voicemail';
}

/** Which way it went, in the words the household's own page already uses for the same fact. */
function wayWords(row: CallRow): string {
  return row.direction === 'inbound' ? 'They rang us' : 'We rang them';
}

export function Calls({ me }: { me: Me }) {
  const wide = useWideView();
  const [from, setFrom] = useState(() => addDays(me.today, -DAYS_SHOWN));
  const [to, setTo] = useState(me.today);
  const [list, setList] = useState<CallList | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!isDay(from) || !isDay(to)) return;
    let live = true;
    setError(null);
    setList(null);
    listCalls(from, to)
      .then((answer) => {
        if (live) setList(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'The calls did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [from, to, attempt]);

  const rows = list?.calls ?? [];

  return (
    <div>
      <ScreenTitle title="Calls" subtitle="Who rang, how it ended, and whose page to open." />
      <OfficeTabs here="calls" />

      <FilterBar>
        <DayBox id="calls-from" label="From" value={from} onChange={setFrom} />
        <DayBox id="calls-to" label="To" value={to} onChange={setTo} />
      </FilterBar>

      {error ? (
        <ErrorNotice title="The calls did not load" message={error.message} onRetry={reload} />
      ) : list === null ? (
        <Busy what="the calls" />
      ) : rows.length === 0 ? (
        <EmptyNotice title="Nobody rang in those days">
          <p>
            Only calls from a household already in the client book are here. A call from somebody
            who is not one yet opens an enquiry instead.
          </p>
        </EmptyNotice>
      ) : (
        <>
          <DataTable
            label="Calls"
            groups={[{ key: 'calls', rows }]}
            columns={opensCallPages(me) ? [...CALL_COLUMNS, theCall(me)] : CALL_COLUMNS}
            rowKey={(row) => row.id}
            card={(row) => <CallCard row={row} me={me} />}
          />
          {wide ? (
            <p className={`mt-2 text-xs text-navy/60 ${MEASURE}`}>
              {rows.length >= list.limit
                ? `The first ${list.limit} are shown. Narrow the days above to see the rest.`
                : 'Newest first. Press a household to open their page, where the words of any voicemail are.'}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

/** One of the two day boxes over the list. A day that is not one leaves the list as it was. */
function DayBox({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (day: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-2.5">
      <label htmlFor={id} className="text-xs leading-4 tracking-wide text-navy/55 uppercase">
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={value}
        onChange={(event) => {
          if (isDay(event.target.value)) onChange(event.target.value);
        }}
        className="min-h-11 desk:min-h-9 w-full rounded-lg border border-navy/25 bg-parchment px-3 text-navy md:w-44"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The table, at md and above. The same rows the cards are drawn from.
 * ------------------------------------------------------------------ */

const CALL_COLUMNS: readonly DataColumn<CallRow>[] = [
  {
    key: 'when',
    header: 'When',
    width: '154px',
    cellClassName: 'font-semibold',
    cell: (row) => shortInstant(row.occurredAt) ?? 'Not said',
  },
  {
    key: 'household',
    header: 'Household',
    cell: (row) => (
      <Link
        to={`/clients/${row.clientId}`}
        className="flex min-h-11 desk:min-h-9 items-center font-serif text-base"
      >
        <span className="break-words">{row.clientName}</span>
      </Link>
    ),
  },
  {
    key: 'who',
    header: 'Whose number',
    width: '140px',
    cellClassName: 'text-navy/75',
    cell: (row) => (
      <div className="flex flex-col items-start gap-1">
        {row.contactName ?? <span className="text-navy/50">Nobody on file</span>}
        <MarkPills marks={row.doNotContact} />
      </div>
    ),
  },
  {
    key: 'way',
    header: 'Which way',
    width: '118px',
    cellClassName: 'text-navy/75',
    cell: (row) => wayWords(row),
  },
  {
    key: 'length',
    header: 'How long',
    width: '112px',
    cellClassName: 'text-navy/75',
    cell: (row) =>
      callLengthWords(row.durationSeconds) ?? <span className="text-navy/50">Not said</span>,
  },
  {
    key: 'outcome',
    header: 'How it ended',
    width: '180px',
    cell: (row) => (
      <div className="flex flex-wrap gap-1.5">
        <OutcomeMark row={row} />
        {row.hasTranscript ? <TranscriptMark /> : null}
      </div>
    ),
  },
];

/**
 * THE WAY ONTO ONE CALL'S PAGE (ops #13), a column of its own and only for an owner or a manager,
 * the two the server opens that page to.
 */
function theCall(me: Me): DataColumn<CallRow> {
  return {
    key: 'call',
    header: 'The call',
    width: '128px',
    cell: (row) => <OpenTheCall me={me} callSid={row.callSid} />,
  };
}

/**
 * How it ended. Coral only where nobody spoke to her, which is the one row on this screen that is
 * a gap somebody has to close, and the same rule her own page keeps for the same fact.
 */
function OutcomeMark({ row }: { row: CallRow }) {
  if (!row.outcome) return <Pill>Still on the call</Pill>;
  if (nobodySpokeToHer(row)) return <GapPill>{CALL_OUTCOME_LABELS[row.outcome]}</GapPill>;
  return <Pill tone="sage">{CALL_OUTCOME_LABELS[row.outcome]}</Pill>;
}

/** One call, on a phone. The whole card opens the household, because that is what it is for. */
function CallCard({ row, me }: { row: CallRow; me: Me }) {
  return (
    <Card>
      <Link to={`/clients/${row.clientId}`} className="block min-h-11 desk:min-h-9">
        <h3 className="font-serif text-lg leading-tight">{row.clientName}</h3>
        <p className="text-sm text-navy/75">
          {wayWords(row)}
          {callLengthWords(row.durationSeconds) ? `, ${callLengthWords(row.durationSeconds)}` : ''}
          {row.contactName ? `, ${row.contactName}` : ''}
        </p>
      </Link>
      <p className="mt-0.5 text-xs text-navy/65">{formatInstant(row.occurredAt)}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <OutcomeMark row={row} />
        {row.hasTranscript ? <TranscriptMark /> : null}
        <MarkPills marks={row.doNotContact} />
      </div>
      {row.hasTranscript ? (
        <p className="mt-2 text-xs text-navy/65">
          What they said is on their own page, under History.
        </p>
      ) : null}
      <OpenTheCall me={me} callSid={row.callSid} className="mt-1" />
    </Card>
  );
}
